import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import clsx from 'clsx'
import {
  IamApi,
  iamErrorMessage,
  organizationDeactivateRequired,
  type OrganizationRef,
} from 'api/IamApi'
import { MainApi } from 'api/MainApi'
import { RobotApi } from 'api/RobotApi'
import { StationApi } from 'api/StationApi'
import { Button } from 'components/Button/Button'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import FavoriteStar from 'components/FavoriteStar/FavoriteStar'
import Loader from 'components/Loader/Loader'
import TablePagination from 'components/TablePagination/TablePagination'
import Title from 'components/Title/Title'
import { hasUnrestrictedDataAccess, useUser } from 'hooks/user'
import { sortRobotsForAssetsList } from 'pages/Assets/assetsRobotsSort'
import { sortStationsForAssetsList } from 'pages/Assets/assetsStationsSort'
import FacilityItem, { IFacilityInfo } from 'pages/FacilityList/FacilityTable/FacilityItem/FacilityItem'
import RobotItem from 'pages/Assets/robots/RobotItem/RobotItem'
import StationItem from 'pages/Assets/stations/StationItem/StationItem'
import { KeyboardEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { PATH, PROGRESS_TYPES } from 'utils/constants'
import { sortWithFavoritesFirst } from 'utils/favoritesSort'
import getTestId from 'utils/getTestId'
import { ILot, IRobotInfo, IStation } from 'utils/interfaces'
import { filterRobotsByEnabledVendors } from 'utils/vendorDisplayName'

import facilityTableStyle from 'pages/FacilityList/FacilityTable/FacilityTable.module.css'
import robotTableStyle from 'pages/Assets/robots/AssetsRobotsTable.module.css'
import stationTableStyle from 'pages/Assets/stations/AssetsStationsTable.module.css'

import style from './AccountFavorites.module.css'

const PAGE_SIZE = 10

type PageState<T> = {
  rows: T[]
  total: number
  progress: PROGRESS_TYPES
}

const emptyPage = <T,>(): PageState<T> => ({
  rows: [],
  total: 0,
  progress: PROGRESS_TYPES.IDLE,
})

const pageCount = (raw: { total?: number; count?: number }, fallback: number) =>
  Number(raw.total || raw.count || fallback) || 0

const AccountFavorites = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useUser()
  const canManageOrganizations = Boolean(
    user && (hasUnrestrictedDataAccess(user.state) || user.state.audience === 'reseller')
  )

  const [robotsOpen, setRobotsOpen] = useState(false)
  const [stationsOpen, setStationsOpen] = useState(false)
  const [facilitiesOpen, setFacilitiesOpen] = useState(false)
  const [organizationsOpen, setOrganizationsOpen] = useState(false)

  const [robotPage, setRobotPage] = useState(1)
  const [stationPage, setStationPage] = useState(1)
  const [facilityPage, setFacilityPage] = useState(1)
  const [organizationPage, setOrganizationPage] = useState(1)
  const [robotPageInput, setRobotPageInput] = useState('1')
  const [stationPageInput, setStationPageInput] = useState('1')
  const [facilityPageInput, setFacilityPageInput] = useState('1')
  const [organizationPageInput, setOrganizationPageInput] = useState('1')

  const [robots, setRobots] = useState<PageState<IRobotInfo>>(emptyPage)
  const [stations, setStations] = useState<PageState<IStation>>(emptyPage)
  const [stationLots, setStationLots] = useState<ILot[]>([])
  const [facilities, setFacilities] = useState<PageState<IFacilityInfo>>(emptyPage)
  const [organizations, setOrganizations] = useState<OrganizationRef[]>([])
  const [organizationsProgress, setOrganizationsProgress] = useState(PROGRESS_TYPES.IDLE)
  const [robotTotal, setRobotTotal] = useState<number | null>(null)
  const [stationTotal, setStationTotal] = useState<number | null>(null)
  const [facilityTotal, setFacilityTotal] = useState<number | null>(null)
  const [pendingDelete, setPendingDelete] = useState<OrganizationRef | null>(null)
  const [deactivateUserCount, setDeactivateUserCount] = useState<number | null>(null)
  const [orgError, setOrgError] = useState<string | null>(null)
  const robotsCatalogLoaded = useRef(false)
  const stationsCatalogLoaded = useRef(false)
  const facilityLoadedPage = useRef<number | null>(null)

  const robotRequest = useRef(0)
  const stationRequest = useRef(0)
  const facilityRequest = useRef(0)

  const favoriteRobotIds = useMemo(
    () => new Set(user?.state.favoriteRobotIds ?? []),
    [user?.state.favoriteRobotIds]
  )
  const favoriteStationIds = useMemo(
    () => new Set(user?.state.favoriteStationIds ?? []),
    [user?.state.favoriteStationIds]
  )
  const favoriteRobotCount = favoriteRobotIds.size
  const favoriteStationCount = favoriteStationIds.size
  const favoriteFacilityCount = user?.state.favoriteFacilityIds.length ?? 0
  const favoriteOrganizationCount = user?.state.favoriteOrganizationIds.length ?? 0

  const sortedRobots = useMemo(
    () => sortWithFavoritesFirst(sortRobotsForAssetsList(robots.rows), favoriteRobotIds),
    [robots.rows, favoriteRobotIds]
  )
  const sortedStations = useMemo(
    () => sortWithFavoritesFirst(sortStationsForAssetsList(stations.rows), favoriteStationIds),
    [stations.rows, favoriteStationIds]
  )
  const visibleRobots = useMemo(
    () => sortedRobots.slice((robotPage - 1) * PAGE_SIZE, robotPage * PAGE_SIZE),
    [sortedRobots, robotPage]
  )
  const visibleStations = useMemo(
    () => sortedStations.slice((stationPage - 1) * PAGE_SIZE, stationPage * PAGE_SIZE),
    [sortedStations, stationPage]
  )

  const loadRobots = useCallback(async () => {
    const request = ++robotRequest.current
    setRobots(current => ({ ...current, progress: PROGRESS_TYPES.WORK }))
    try {
      const response = await RobotApi.fetchRobots(1, 1000, undefined, undefined, undefined, undefined, true)
      if (robotRequest.current !== request) return
      const rows = filterRobotsByEnabledVendors(response.results)
      setRobotTotal(rows.length)
      setRobots({ rows, total: rows.length, progress: PROGRESS_TYPES.SUCCESS })
      robotsCatalogLoaded.current = true
    } catch (error) {
      console.error('Failed to load robots', error)
      if (robotRequest.current !== request) return
      setRobots(current => ({ ...current, progress: PROGRESS_TYPES.ERROR }))
    }
  }, [])

  const loadStations = useCallback(async () => {
    const request = ++stationRequest.current
    setStations(current => ({ ...current, progress: PROGRESS_TYPES.WORK }))
    try {
      const [stationRows, lots] = await Promise.all([StationApi.fetchStations(), StationApi.fetchLots()])
      if (stationRequest.current !== request) return
      setStationLots(lots)
      setStationTotal(stationRows.length)
      setStations({ rows: stationRows, total: stationRows.length, progress: PROGRESS_TYPES.SUCCESS })
      stationsCatalogLoaded.current = true
    } catch (error) {
      console.error('Failed to load stations', error)
      if (stationRequest.current !== request) return
      setStations(current => ({ ...current, progress: PROGRESS_TYPES.ERROR }))
    }
  }, [])

  const loadFacilities = useCallback(async (page: number) => {
    const request = ++facilityRequest.current
    setFacilities(current => ({ ...current, progress: PROGRESS_TYPES.WORK }))
    try {
      const response = await MainApi.fetchFacilities(page, PAGE_SIZE)
      if (facilityRequest.current !== request) return
      const total = pageCount(response, response.results.length)
      setFacilityTotal(total)
      setFacilities({
        rows: response.results as IFacilityInfo[],
        total,
        progress: PROGRESS_TYPES.SUCCESS,
      })
    } catch (error) {
      console.error('Failed to load facilities', error)
      if (facilityRequest.current !== request) return
      setFacilities(current => ({ ...current, progress: PROGRESS_TYPES.ERROR }))
    }
  }, [])

  const loadOrganizations = useCallback(async () => {
    setOrganizationsProgress(PROGRESS_TYPES.WORK)
    try {
      setOrganizations(await IamApi.listOrganizations())
      setOrganizationsProgress(PROGRESS_TYPES.SUCCESS)
    } catch (error) {
      console.error('Failed to load organizations', error)
      setOrganizationsProgress(PROGRESS_TYPES.ERROR)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    void Promise.all([
      RobotApi.fetchRobots(1, 1, undefined, undefined, undefined, undefined, true),
      StationApi.fetchStationsPage(1, 1),
      MainApi.fetchFacilities(1, 1),
    ])
      .then(([robotPageResponse, stationPageResponse, facilityPageResponse]) => {
        if (cancelled) return
        setRobotTotal(robotPageResponse.count)
        setStationTotal(stationPageResponse.count)
        setFacilityTotal(pageCount(facilityPageResponse, facilityPageResponse.results.length))
      })
      .catch(error => {
        console.error('Failed to load favorite catalog totals', error)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (canManageOrganizations && organizationsProgress === PROGRESS_TYPES.IDLE) {
      void loadOrganizations()
    }
  }, [canManageOrganizations, organizationsProgress, loadOrganizations])

  useEffect(() => {
    if (!robotsOpen || robotsCatalogLoaded.current) return
    void loadRobots()
  }, [robotsOpen, loadRobots])

  useEffect(() => {
    if (!stationsOpen || stationsCatalogLoaded.current) return
    void loadStations()
  }, [stationsOpen, loadStations])

  useEffect(() => {
    if (!facilitiesOpen || facilityLoadedPage.current === facilityPage) return
    facilityLoadedPage.current = facilityPage
    void loadFacilities(facilityPage)
  }, [facilitiesOpen, facilityPage, loadFacilities])

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(sortedRobots.length / PAGE_SIZE) || 1)
    if (robotPage > maxPage) {
      setRobotPage(maxPage)
      setRobotPageInput(String(maxPage))
    }
  }, [robotPage, sortedRobots.length])

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(sortedStations.length / PAGE_SIZE) || 1)
    if (stationPage > maxPage) {
      setStationPage(maxPage)
      setStationPageInput(String(maxPage))
    }
  }, [stationPage, sortedStations.length])

  const jumpToPage = (
    event: KeyboardEvent<HTMLInputElement>,
    total: number,
    setPage: (page: number) => void
  ) => {
    if (event.key !== 'Enter') return
    const next = Number(event.currentTarget.value)
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
    if (Number.isInteger(next) && next >= 1 && next <= totalPages) setPage(next)
  }

  const organizationRows = organizations.slice((organizationPage - 1) * PAGE_SIZE, organizationPage * PAGE_SIZE)

  const closeDelete = () => {
    setPendingDelete(null)
    setDeactivateUserCount(null)
  }

  const onDeleteOrganization = async () => {
    if (!pendingDelete) return
    setOrgError(null)
    try {
      await IamApi.deleteOrganization(pendingDelete.id, deactivateUserCount != null)
      closeDelete()
      setOrganizations(current => current.filter(item => item.id !== pendingDelete.id))
    } catch (error) {
      const userCount = organizationDeactivateRequired(error)
      if (userCount != null && deactivateUserCount == null) {
        setDeactivateUserCount(userCount)
        return
      }
      closeDelete()
      setOrgError(iamErrorMessage(error, 'detail') || iamErrorMessage(error) || t('account.organizations_delete_error'))
    }
  }

  return (
    <div className={style.panel} data-test-id={getTestId('account-favorites')}>
      <div className={style.header}>
        <Title text={t('account.favorites_title')} />
        <div className={style.subtitle}>{t('account.favorites_subtitle')}</div>
      </div>

      <CatalogSection
        title={t('account.favorites_robots')}
        favorites={favoriteRobotCount}
        total={robotTotal}
        open={robotsOpen}
        onToggle={() => setRobotsOpen(value => !value)}
        testId="account-favorites-robots"
      >
        <CatalogBody
          progress={robots.progress}
          empty={sortedRobots.length === 0}
          emptyText={t('robots.no_robots_title')}
          errorText={t('account.favorites_load_error')}
        >
          <div className={clsx(robotTableStyle.tableWrapper, style.catalogTable)}>
            <div className={robotTableStyle.tableHeader}>
              <div />
              <div className={clsx(robotTableStyle.tableHeaderItem, 'text-16-400')}>{t('robots.serial_id')}</div>
              <div className={clsx(robotTableStyle.tableHeaderItem, 'text-16-400')}>{t('robots.name')}</div>
              <div className={clsx(robotTableStyle.tableHeaderItem, 'text-16-400')}>{t('robots.battery')}</div>
              <div className={clsx(robotTableStyle.tableHeaderItemCentered, 'text-16-400')}>{t('robots.mode')}</div>
              <div />
            </div>
            <div className={robotTableStyle.table}>
              {visibleRobots.map(robot => (
                <RobotItem
                  key={robot.id}
                  robot={robot}
                  robots={sortedRobots}
                  stations={[]}
                  onRobotUpdated={() => void loadRobots()}
                  onStationsChanged={() => undefined}
                  hightlighted={false}
                />
              ))}
            </div>
          </div>
          <CatalogPager
            page={robotPage}
            total={sortedRobots.length}
            pageInput={robotPageInput}
            onPageInputChange={setRobotPageInput}
            onJump={event => jumpToPage(event, sortedRobots.length, setRobotPage)}
            onBlur={() => setRobotPageInput(String(robotPage))}
            onPrev={() =>
              setRobotPage(page => {
                const next = Math.max(1, page - 1)
                setRobotPageInput(String(next))
                return next
              })
            }
            onNext={() =>
              setRobotPage(page => {
                const next = page + 1
                setRobotPageInput(String(next))
                return next
              })
            }
          />
        </CatalogBody>
      </CatalogSection>

      <CatalogSection
        title={t('account.favorites_stations')}
        favorites={favoriteStationCount}
        total={stationTotal}
        open={stationsOpen}
        onToggle={() => setStationsOpen(value => !value)}
        testId="account-favorites-stations"
      >
        <CatalogBody
          progress={stations.progress}
          empty={sortedStations.length === 0}
          emptyText={t('assets.no_stations_title')}
          errorText={t('account.favorites_load_error')}
        >
          <div className={clsx(stationTableStyle.tableWrapper, style.catalogTable)}>
            <div className={stationTableStyle.tableHeader}>
              <div />
              <div className={clsx(stationTableStyle.tableHeaderItem, 'text-16-400')}>{t('robots.serial_id')}</div>
              <div className={clsx(stationTableStyle.tableHeaderItem, 'text-16-400')}>{t('robots.name')}</div>
              <div className={clsx(stationTableStyle.tableHeaderItem, 'text-16-400')}>Lots</div>
              <div className={clsx(stationTableStyle.tableHeaderItem, 'text-16-400')}>Status</div>
              <div />
            </div>
            <div className={stationTableStyle.table}>
              {visibleStations.map(station => (
                <StationItem
                  key={station.id}
                  station={station}
                  stations={sortedStations}
                  lots={stationLots}
                  onStationUpdated={() => void loadStations()}
                  onStationsChanged={() => void loadStations()}
                  hightlighted={false}
                />
              ))}
            </div>
          </div>
          <CatalogPager
            page={stationPage}
            total={sortedStations.length}
            pageInput={stationPageInput}
            onPageInputChange={setStationPageInput}
            onJump={event => jumpToPage(event, sortedStations.length, setStationPage)}
            onBlur={() => setStationPageInput(String(stationPage))}
            onPrev={() =>
              setStationPage(page => {
                const next = Math.max(1, page - 1)
                setStationPageInput(String(next))
                return next
              })
            }
            onNext={() =>
              setStationPage(page => {
                const next = page + 1
                setStationPageInput(String(next))
                return next
              })
            }
          />
        </CatalogBody>
      </CatalogSection>

      <CatalogSection
        title={t('account.favorites_facilities')}
        favorites={favoriteFacilityCount}
        total={facilityTotal}
        open={facilitiesOpen}
        onToggle={() => setFacilitiesOpen(value => !value)}
        testId="account-favorites-facilities"
      >
        <CatalogBody
          progress={facilities.progress}
          empty={facilities.rows.length === 0}
          emptyText={t('favorites.empty_facilities')}
          errorText={t('account.favorites_load_error')}
        >
          <div className={clsx(facilityTableStyle.tableWrapper, style.catalogTable)}>
            <div className={facilityTableStyle.tableHeader}>
              <div />
              <div className={clsx(facilityTableStyle.tableHeaderItemSmall, 'text-16-400')}>Name</div>
              <div className={clsx(facilityTableStyle.tableHeaderItemSmall, 'text-16-400')}>Type</div>
              <div className={clsx(facilityTableStyle.tableHeaderItemSmall, 'text-16-400')}>Address</div>
              <div className={clsx(facilityTableStyle.tableHeaderItemSmall, 'text-16-400')}>Fleet</div>
              <div />
            </div>
            <div className={facilityTableStyle.table}>
              {facilities.rows.map(facility => (
                <FacilityItem
                  key={facility.id}
                  facility={facility}
                  robots={[]}
                  onFacilitiesUpdated={() => void loadFacilities(facilityPage)}
                />
              ))}
            </div>
          </div>
          <CatalogPager
            page={facilityPage}
            total={facilities.total}
            pageInput={facilityPageInput}
            onPageInputChange={setFacilityPageInput}
            onJump={event => jumpToPage(event, facilities.total, setFacilityPage)}
            onBlur={() => setFacilityPageInput(String(facilityPage))}
            onPrev={() =>
              setFacilityPage(page => {
                const next = Math.max(1, page - 1)
                setFacilityPageInput(String(next))
                return next
              })
            }
            onNext={() =>
              setFacilityPage(page => {
                const next = page + 1
                setFacilityPageInput(String(next))
                return next
              })
            }
          />
        </CatalogBody>
      </CatalogSection>

      {canManageOrganizations ? (
        <CatalogSection
          title={t('account.favorites_organizations')}
          favorites={favoriteOrganizationCount}
          total={organizationsProgress === PROGRESS_TYPES.SUCCESS ? organizations.length : null}
          open={organizationsOpen}
          onToggle={() => setOrganizationsOpen(value => !value)}
          testId="account-favorites-organizations"
        >
          <CatalogBody
            progress={organizationsProgress}
            empty={organizationRows.length === 0}
            emptyText={t('account.organizations_empty')}
            errorText={t('account.favorites_load_error')}
          >
            {orgError ? <div className={style.errorText}>{orgError}</div> : null}
            <div className={style.orgHeader}>
              <div />
              <div>{t('account.organizations_name')}</div>
              <div className={style.hideMobile}>{t('account.organizations_people')}</div>
              <div className={style.hideMobile}>{t('account.organizations_status')}</div>
              <div />
            </div>
            {organizationRows.map(org => (
              <div className={style.orgRow} key={org.id}>
                <FavoriteStar kind="organization" id={org.id} />
                <div className={style.cell}>{org.name}</div>
                <div className={clsx(style.cell, style.hideMobile)}>{org.member_count}</div>
                <div className={clsx(style.cell, style.hideMobile)}>
                  {org.is_active ? t('account.organizations_active') : t('account.organizations_inactive')}
                </div>
                <div className={style.actions}>
                  {org.is_active ? (
                    <Button.Outlined
                      onClick={() => navigate(`${PATH.ACCOUNT_ORGANIZATIONS}?org=${org.id}`)}
                      dataTestId={`favorites-org-edit-${org.id}`}
                    >
                      {t('common.edit')}
                    </Button.Outlined>
                  ) : null}
                  {org.is_active ? (
                    <Button.Outlined
                      onClick={() => {
                        setDeactivateUserCount(null)
                        setPendingDelete(org)
                      }}
                      dataTestId={`favorites-org-delete-${org.id}`}
                    >
                      {t('account.organizations_delete')}
                    </Button.Outlined>
                  ) : null}
                </div>
              </div>
            ))}
            <CatalogPager
              page={organizationPage}
              total={organizations.length}
              pageInput={organizationPageInput}
              onPageInputChange={setOrganizationPageInput}
              onJump={event => jumpToPage(event, organizations.length, setOrganizationPage)}
              onBlur={() => setOrganizationPageInput(String(organizationPage))}
              onPrev={() =>
                setOrganizationPage(page => {
                  const next = Math.max(1, page - 1)
                  setOrganizationPageInput(String(next))
                  return next
                })
              }
              onNext={() =>
                setOrganizationPage(page => {
                  const next = page + 1
                  setOrganizationPageInput(String(next))
                  return next
                })
              }
            />
          </CatalogBody>
        </CatalogSection>
      ) : null}

      <ConfirmDialog
        isOpen={pendingDelete != null}
        onClose={closeDelete}
        onConfirmClick={onDeleteOrganization}
        title={t('account.organizations_delete_title')}
        confirmText={t('account.organizations_delete')}
        danger
      >
        {deactivateUserCount == null
          ? t('account.organizations_delete_body')
          : t('account.organizations_delete_users_body', { count: deactivateUserCount })}
      </ConfirmDialog>
    </div>
  )
}

const CatalogSection = ({
  title,
  favorites,
  total,
  open,
  onToggle,
  testId,
  children,
}: {
  title: string
  favorites: number
  total: number | null
  open: boolean
  onToggle: () => void
  testId: string
  children: ReactNode
}) => {
  const { t } = useTranslation()
  const countLabel =
    total == null
      ? t('account.favorites_section_favorites_only', { favorites })
      : t('account.favorites_section_counts', { favorites, total })
  const countTitle =
    total == null
      ? t('account.favorites_section_favorites_only_hint', { favorites })
      : t('account.favorites_section_counts_hint', { favorites, total })
  return (
    <section className={style.section} data-test-id={getTestId(testId)}>
      <button type="button" className={style.sectionToggle} aria-expanded={open} onClick={onToggle}>
        {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        <span className={style.sectionTitle}>{title}</span>
        <span className={style.sectionCount} title={countTitle}>
          {countLabel}
        </span>
      </button>
      {open ? children : null}
    </section>
  )
}

const CatalogBody = ({
  progress,
  empty,
  emptyText,
  errorText,
  children,
}: {
  progress: PROGRESS_TYPES
  empty: boolean
  emptyText: string
  errorText: string
  children: ReactNode
}) => {
  if (progress === PROGRESS_TYPES.WORK || progress === PROGRESS_TYPES.IDLE) return <Loader />
  if (progress === PROGRESS_TYPES.ERROR) return <div className={style.empty}>{errorText}</div>
  if (empty) return <div className={style.empty}>{emptyText}</div>
  return <>{children}</>
}

const CatalogPager = ({
  page,
  total,
  pageInput,
  onPageInputChange,
  onJump,
  onBlur,
  onPrev,
  onNext,
}: {
  page: number
  total: number
  pageInput: string
  onPageInputChange: (value: string) => void
  onJump: (event: KeyboardEvent<HTMLInputElement>) => void
  onBlur: () => void
  onPrev: () => void
  onNext: () => void
}) => {
  if (total <= PAGE_SIZE) return null
  return (
    <TablePagination
      page={page}
      totalPages={Math.ceil(total / PAGE_SIZE) || 1}
      pageInput={pageInput}
      onPageInputChange={onPageInputChange}
      onPageInputKeyDown={onJump}
      onPageInputBlur={onBlur}
      onPrev={onPrev}
      onNext={onNext}
    />
  )
}

export default AccountFavorites
